import React from 'react';
import { useTranslation } from 'react-i18next';
import { RatingsTrendIcon } from '@/pages/Overview/RatingsTrendIcon';
import { StapGridlines } from '@/pages/Overview/StapGridlines';
import { StapLabels } from '@/pages/Overview/StapLabels';
import {
  getValueColumnClass,
  isAchievableChart,
  normalizeEvaluation,
} from '@/pages/Overview/ratingsChart';
import type {
  DisciplineEvaluation,
  EvaluationChart,
  EvaluationChartType,
  EvaluationData,
} from '@/types/ratings';
import { compareLatvian, toPercentage } from '@/utils/text';

interface Props {
  chart: EvaluationChart;
}

interface LegendItemProps {
  colorClassName: string;
  label: string;
}

const LegendItem: React.FC<LegendItemProps> = ({ colorClassName, label }) => (
  <div className="flex items-center gap-1">
    <span className={`size-3 shrink-0 rounded-[4px] ${colorClassName}`} />
    <span className="text-body-13 text-subtle-foreground">{label}</span>
  </div>
);

interface RowProps {
  entry: DisciplineEvaluation;
  type: EvaluationChartType;
  isAchievable: boolean;
}

const SubjectRow: React.FC<RowProps> = ({ entry, type, isAchievable }) => {
  const isAbove = entry.isStudentEvaluationHigherOrEqualToClassEvaluation;
  const studentFactor = normalizeEvaluation(
    entry.averageStudentEvaluation?.value ?? 0,
    type,
  );
  const classFactor = normalizeEvaluation(
    entry.averageClassEvaluation?.value ?? 0,
    type,
  );
  const valueColClass = getValueColumnClass(type);

  return (
    <div className="flex items-center gap-2">
      <p className="line-clamp-2 w-1/4 shrink-0 text-body-14 text-foreground md:w-[20%]">
        {entry.disciplineName}
      </p>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {!isAchievable && (
          <span
            className={`${valueColClass} shrink-0 text-right text-subhead-14 text-foreground`}
          >
            {entry.averageStudentEvaluation?.stringValue}
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-clip">
          <span
            style={{ width: toPercentage(studentFactor * 100) }}
            className={`h-3 rounded-r-[6px] ${isAbove ? 'bg-green' : 'bg-primary'}`}
          />
          <span
            style={{ width: toPercentage(classFactor * 100) }}
            className="h-2.5 rounded-r-[5px] bg-gray"
          />
        </div>
        {!isAchievable && (
          <span
            className={`${valueColClass} shrink-0 text-right text-body-14 text-muted-foreground`}
          >
            {entry.averageClassEvaluation?.stringValue}
          </span>
        )}
      </div>
    </div>
  );
};

export const SubjectChartCard: React.FC<Props> = ({ chart }) => {
  const { t } = useTranslation();
  const discipline = chart.charts.averageEvaluationsByDisciplineChart;
  const isAchievable = isAchievableChart(chart.type);

  const sortedValues = [...(discipline?.chartValues ?? [])].sort((a, b) =>
    compareLatvian(a.disciplineName, b.disciplineName),
  );

  const studentTotal: EvaluationData | null =
    discipline?.averageStudentEvaluationTotal ?? null;
  const classTotal: EvaluationData | null =
    discipline?.averageClassEvaluationTotal ?? null;
  const isPositive = (studentTotal?.value ?? 0) >= (classTotal?.value ?? 0);

  const rows = sortedValues.map((entry) => (
    <SubjectRow
      key={entry.disciplineId}
      entry={entry}
      type={chart.type}
      isAchievable={isAchievable}
    />
  ));

  return (
    <div className="flex flex-col rounded-xl bg-popover p-6 shadow-card">
      {!isAchievable && (
        <div className="flex items-center justify-between gap-2 md:justify-start">
          <p className="max-w-[88px] text-body-13 text-muted-foreground md:max-w-none md:w-[18.6%] md:shrink-0">
            {t('overview.ratings.averageMark')}
          </p>
          <div className="contents md:flex md:flex-1 md:items-center md:justify-between md:gap-2">
            <div className="flex items-center gap-1">
              <span className="text-body-13 text-subtle-foreground">
                {t('overview.ratings.my')}
              </span>
              <span className="text-subhead-14 text-foreground">
                {studentTotal?.stringValue}
              </span>
              <RatingsTrendIcon isPositive={isPositive} />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-body-13 text-subtle-foreground">
                {t('overview.ratings.class')}
              </span>
              <span className="text-subhead-14 text-muted-foreground">
                {classTotal?.stringValue}
              </span>
            </div>
          </div>
        </div>
      )}

      {!isAchievable && (
        <div className="my-6 flex flex-col gap-6 border-y border-border-strong/8 py-6">
          {rows}
        </div>
      )}

      {isAchievable && (
        <div className="mb-6 border-b border-border-strong/8 pt-3 pb-3">
          <div className="relative flex flex-col gap-6">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 flex gap-2"
            >
              <span className="w-1/4 shrink-0 md:w-[20%]" />
              <div className="relative flex-1">
                <StapGridlines />
              </div>
            </div>
            {rows}
            <div className="flex gap-2">
              <span className="w-1/4 shrink-0 md:w-[20%]" />
              <div className="h-px flex-1 bg-border-strong/8" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <span className="w-1/4 shrink-0 md:w-[20%]" />
            <div className="relative h-5 flex-1 text-body-14 text-subtle-foreground">
              <StapLabels />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:gap-6">
        <LegendItem
          colorClassName="bg-green"
          label={t('overview.ratings.legend.aboveAverage')}
        />
        <LegendItem
          colorClassName="bg-primary"
          label={t('overview.ratings.legend.belowAverage')}
        />
        <LegendItem
          colorClassName="bg-gray"
          label={t('overview.ratings.legend.classAverage')}
        />
      </div>
    </div>
  );
};
